import { useRef, useState } from 'react';

// Tiny OPSORA badge anchored to the bottom-left corner.
//
// Two interaction modes:
//   • Single click  → confirm-then-sign-out flow (return to pairing page)
//   • Five quick clicks within 800ms each → if we're running inside the
//     native Opsora Player APK, open the hidden PIN-protected settings.
//     Outside the native app, the gesture is a no-op (settings only exist
//     on a real device).
//
// The native app exposes `window.OpsoraNative` (see OpsoraJsBridge.kt). We
// feature-detect; nothing happens in a normal browser tab.
export default function CornerBadge({ onSignOut }) {
  const [confirming, setConfirming] = useState(false);
  const tapsRef = useRef([]);
  const TAP_WINDOW_MS = 800;
  const TAPS_NEEDED   = 5;

  function handleClick() {
    // Track tap timestamps; collapse to last TAPS_NEEDED within the window.
    const now = Date.now();
    tapsRef.current = [...tapsRef.current, now].filter(t => now - t <= TAP_WINDOW_MS * (TAPS_NEEDED - 1));
    if (tapsRef.current.length >= TAPS_NEEDED) {
      tapsRef.current = [];
      if (typeof window !== 'undefined' && window.OpsoraNative?.openSettings) {
        try { window.OpsoraNative.openSettings(); return; } catch {}
      }
      // In a regular browser the gesture has no destination; fall through to
      // the sign-out confirm so the multi-tap still does *something*.
    }
    setConfirming(true);
  }

  function cancel() {
    setConfirming(false);
  }

  function confirmSignOut() {
    setConfirming(false);
    // Clear any player token; safe even if not paired (shortcut codes don't set one)
    try { localStorage.removeItem('player:token'); } catch {}
    try { localStorage.removeItem('player:lastPlayback'); } catch {}
    if (onSignOut) onSignOut();
    // Go back to root pair screen
    window.location.replace('/');
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        title="Sign out of this screen"
        aria-label="Sign out of this screen"
        style={S.badge}
      >
        <span style={S.dot}>▶</span>
        <span style={S.mark}>OPSORA</span>
      </button>

      {confirming && (
        <div style={S.modalScrim} onClick={cancel}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalTitle}>Sign out of this screen?</div>
            <div style={S.modalBody}>
              The TV will return to the pairing screen and you'll need to enter a code again.
            </div>
            <div style={S.modalBtnRow}>
              <button style={S.btnCancel} onClick={cancel}>Stay signed in</button>
              <button style={S.btnPrimary} onClick={confirmSignOut} autoFocus>Sign out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const S = {
  badge: {
    position: 'fixed',
    left: 14,
    bottom: 12,
    zIndex: 9999,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 9px',
    background: 'rgba(0,0,0,0.35)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 999,
    color: 'rgba(255,255,255,0.55)',
    fontFamily: "'Space Mono', monospace",
    fontSize: 10,
    letterSpacing: 2,
    cursor: 'pointer',
    opacity: 0.5,
    transition: 'opacity .2s ease, background .2s ease, color .2s ease',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  dot:  { fontSize: 7, color: '#a78bfa' },
  mark: { fontWeight: 700 },

  modalScrim: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  },
  modal: {
    background: '#130f35',
    border: '1px solid rgba(124,58,237,0.4)',
    borderRadius: 18,
    padding: '28px 32px',
    maxWidth: 440,
    color: '#fff',
    fontFamily: 'system-ui, sans-serif',
    boxShadow: '0 30px 80px rgba(0,0,0,.6)',
  },
  modalTitle:  { fontSize: 22, fontWeight: 800, marginBottom: 8 },
  modalBody:   { fontSize: 14, color: '#9ca3af', lineHeight: 1.5, marginBottom: 22 },
  modalBtnRow: { display: 'flex', gap: 12, justifyContent: 'flex-end' },
  btnPrimary:  {
    background: '#7c3aed', border: 'none', borderRadius: 10,
    padding: '10px 22px', fontSize: 14, fontWeight: 700, color: '#fff',
    cursor: 'pointer',
  },
  btnCancel: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
    padding: '10px 22px', fontSize: 14, fontWeight: 600, color: '#9ca3af',
    cursor: 'pointer',
  },
};
